import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useCms } from "./CmsContext";

/** Side-by-side live preview of the site in draft mode. */
export function PreviewPane({ sitePath }) {
  const { previewUrl, previewVersion, config } = useCms();
  const url = previewUrl(sitePath);

  if (!url) {
    return (
      <div className="lw-preview lw-preview--unavailable">
        <p className="lw-empty">
          Live preview isn’t configured yet
          {!config.previewToken ? " (missing preview token on the server)" : ""}.
        </p>
      </div>
    );
  }

  return (
    <div className="lw-preview">
      <div className="lw-preview__bar">
        <span className="lw-preview__path">{sitePath}</span>
        <a className="lw-preview__open" href={url} target="_blank" rel="noopener noreferrer">
          Open in new tab ↗
        </a>
      </div>
      {/* key forces a reload after (debounced) saves */}
      <iframe key={previewVersion} className="lw-preview__frame" title="Site preview" src={url} />
    </div>
  );
}

const PREVIEW_PREF_KEY = "lwcms-preview-open";

/**
 * Shared editor layout: header with back link / title / actions,
 * dirty-state save bar, optional side-by-side preview.
 */
export function EditorLayout({
  backTo,
  backLabel,
  title,
  subtitle,
  meta,
  dirty,
  saving,
  onSave,
  sitePath,
  headerExtra,
  children,
}) {
  const [previewOpen, setPreviewOpen] = useState(
    () => localStorage.getItem(PREVIEW_PREF_KEY) === "true"
  );

  useEffect(() => {
    localStorage.setItem(PREVIEW_PREF_KEY, String(previewOpen));
  }, [previewOpen]);

  // Cmd/Ctrl+S to save
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        if (dirty && !saving) onSave();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dirty, saving, onSave]);

  return (
    <div className={`lw-editor ${previewOpen ? "lw-editor--split" : ""}`}>
      <div className="lw-editor__main">
        <div className="lw-editor__head">
          <div className="lw-editor__headleft">
            {backTo && (
              <Link to={backTo} className="lw-backlink">
                ← {backLabel || "Back"}
              </Link>
            )}
            <h1 className="lw-editor__title">{title}</h1>
            {subtitle && <p className="lw-editor__subtitle">{subtitle}</p>}
            {meta && <p className="lw-editor__meta">{meta}</p>}
          </div>
          <div className="lw-editor__actions">
            {headerExtra}
            {sitePath && (
              <button
                type="button"
                className={`lw-btn lw-btn--secondary lw-btn--sm ${previewOpen ? "lw-btn--active" : ""}`}
                onClick={() => setPreviewOpen((v) => !v)}
              >
                {previewOpen ? "Hide preview" : "Live preview"}
              </button>
            )}
            <button
              type="button"
              className="lw-btn lw-btn--primary"
              disabled={!dirty || saving}
              onClick={onSave}
            >
              {saving ? "Saving…" : dirty ? "Save draft" : "Saved"}
            </button>
          </div>
        </div>
        {dirty && (
          <p className="lw-editor__dirtyhint">
            Unsaved changes — save to update the draft (⌘S). Drafts only go live when you publish.
          </p>
        )}
        <div className="lw-editor__body">{children}</div>
      </div>
      {previewOpen && sitePath && (
        <div className="lw-editor__preview">
          <PreviewPane sitePath={sitePath} />
        </div>
      )}
    </div>
  );
}

/** Simple page-level header for list pages. */
export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="lw-pagehead">
      <div>
        <h1 className="lw-pagehead__title">{title}</h1>
        {subtitle && <p className="lw-pagehead__subtitle">{subtitle}</p>}
      </div>
      {actions && <div className="lw-pagehead__actions">{actions}</div>}
    </div>
  );
}
